import type { OmniboxEnrichment } from "@/contexts/OmniboxEnrichmentContext.tsx";
import type { CalendarContext } from "@/types/threadEvents.ts";

/**
 * Run every registered enrichment's `buildMessage` over the raw user text and
 * collect the resulting agent-facing payload.
 *
 * `agentContent` is text the actor sees inline alongside the user prompt
 * (e.g. "User selected these contacts: …"); `calendarContext` is a structured
 * payload the server expands into agent content by fetching events itself
 * (post-BIG-4941). Multiple agentContents are joined with newlines;
 * calendarContexts are mutually exclusive at runtime (calendar enrichments
 * coordinate via `hasManualSelections`), so we take the last one and dev-warn
 * if more than one shows up.
 *
 * Shared between the main `Omnibox` and the slim prompt inputs hosted inside
 * the calendar's contextual-agent-trigger popovers — both submit through the
 * same enrichment pipeline so context (selected ranges, implicit view, etc.)
 * flows uniformly.
 */
export function applyEnrichments(
  initialText: string,
  enrichments: OmniboxEnrichment[],
): {
  text: string;
  agentContent: string | undefined;
  calendarContext: CalendarContext | undefined;
} {
  let text = initialText;
  const agentContentParts: string[] = [];
  let calendarContext: CalendarContext | undefined;
  for (const enrichment of enrichments) {
    const result = enrichment.buildMessage?.(text);
    if (!result) continue;
    text = result.text;
    if (result.agentContent) agentContentParts.push(result.agentContent);
    if (result.calendarContext) {
      if (calendarContext) {
        console.warn(
          "Multiple enrichments supplied calendarContext; keeping last. Enrichments should be mutually exclusive.",
        );
      }
      calendarContext = result.calendarContext;
    }
  }
  return {
    text,
    agentContent: agentContentParts.length > 0 ? agentContentParts.join("\n") : undefined,
    calendarContext,
  };
}
